import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { Truck } from "lucide-react";
import toast from "react-hot-toast";
import { AppModalFooter, AppModalShell } from "../../components/app-ui.js";
import { Button } from "../../components/legacy-ui.js";
import { upsertMilkDelivery, upsertMilkDayTrips } from "../../utils/api.js";
import { parseEsVeNumber } from "../../utils/formatters.js";
import { normalizeDiscountScope, splitDayDiscount } from "../../utils/milkUtils.js";

const fmtLiters = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const inputClass =
  "w-full min-w-[5rem] text-right text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30 tabular-nums";

const dateKey = (value) => String(value || "").slice(0, 10);

function litersToInput(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return String(n);
}

function parseQty(raw) {
  const typed = String(raw || "").trim();
  if (!typed) return 0;
  const n = parseEsVeNumber(typed);
  if (n == null || n < 0) return null;
  return n;
}

function normalizeTrips(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((trip) => ({
      vaca: Math.round(Math.max(0, Number(trip?.vaca) || 0) * 100) / 100,
      bufalo: Math.round(Math.max(0, Number(trip?.bufalo) || 0) * 100) / 100,
    }))
    .filter((trip) => trip.vaca > 0 || trip.bufalo > 0);
}

function tripKey(trips) {
  return normalizeTrips(trips)
    .map((trip) => `${trip.vaca}|${trip.bufalo}`)
    .join(";");
}

function sumTrips(trips, field) {
  return normalizeTrips(trips).reduce((sum, trip) => sum + (trip[field] || 0), 0);
}

function tripButtonLabel(count) {
  if (count === 1) return "1 viaje";
  if (count > 1) return `${count} viajes`;
  return "Viajes";
}

function dayLabel(date, days = []) {
  const key = dateKey(date);
  const found = days.find((d) => d.date === key);
  if (found) {
    return `${found.shortLabel || found.label || ""} ${format(new Date(`${key}T12:00:00`), "dd/MM")}`.trim();
  }
  if (key.length >= 10) {
    return format(new Date(`${key}T12:00:00`), "EEEE dd/MM", { locale: undefined });
  }
  return key;
}

function buildDraft(providers, date) {
  const key = dateKey(date);
  return [...providers]
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "es"))
    .map((provider) => {
      const day = provider?.daily?.[key] || {};
      const trips = normalizeTrips(day.trips);
      const vaca = trips.length ? sumTrips(trips, "vaca") : Number(day.vaca || 0);
      const bufalo = trips.length ? sumTrips(trips, "bufalo") : Number(day.bufalo || 0);
      return {
        id: provider.id,
        name: provider.name || "",
        origVaca: Number(day.vaca || 0),
        origBufalo: Number(day.bufalo || 0),
        origDiscount: Number(day.discount || 0),
        origDiscountScope: normalizeDiscountScope(day.discountScope),
        origTrips: trips,
        vaca: litersToInput(vaca),
        bufalo: litersToInput(bufalo),
        trips,
      };
    });
}

function DayTripsEditor({ dayLabelText, initialTrips, onClose, onApply }) {
  const [rows, setRows] = useState(() => {
    const source = initialTrips.length ? initialTrips : [{ vaca: "", bufalo: "" }];
    return source.map((trip) => ({
      vaca: trip.vaca ? String(trip.vaca) : "",
      bufalo: trip.bufalo ? String(trip.bufalo) : "",
    }));
  });
  const [error, setError] = useState("");

  const totals = rows.reduce(
    (acc, row) => ({
      vaca: acc.vaca + (parseQty(row.vaca) || 0),
      bufalo: acc.bufalo + (parseQty(row.bufalo) || 0),
    }),
    { vaca: 0, bufalo: 0 },
  );

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    setError("");
  };

  const apply = (keep) => {
    if (!keep) {
      onApply([]);
      return;
    }
    for (let i = 0; i < rows.length; i += 1) {
      const vacaRaw = String(rows[i].vaca || "").trim();
      const bufaloRaw = String(rows[i].bufalo || "").trim();
      if (vacaRaw && parseQty(vacaRaw) == null) {
        setError(`Número inválido en vaca, viaje ${i + 1}.`);
        return;
      }
      if (bufaloRaw && parseQty(bufaloRaw) == null) {
        setError(`Número inválido en búfala, viaje ${i + 1}.`);
        return;
      }
    }
    const cleaned = [];
    rows.forEach((row) => {
      const vaca = parseQty(row.vaca) || 0;
      const bufalo = parseQty(row.bufalo) || 0;
      if (vaca <= 0 && bufalo <= 0) return;
      cleaned.push({
        vaca: Math.round(vaca * 100) / 100,
        bufalo: Math.round(bufalo * 100) / 100,
      });
    });
    onApply(cleaned);
  };

  return (
    <AppModalShell
      open
      onClose={onClose}
      title={`Viajes — ${dayLabelText}`}
      widthClass="max-w-md w-full"
      fitContent
      zClass="z-[90]"
      footer={
        <AppModalFooter>
          <Button variant="secondary" onClick={() => apply(false)}>
            Quitar viajes
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => apply(true)}>Usar totales</Button>
          </div>
        </AppModalFooter>
      }
    >
      <p className="text-sm text-gray-500 mb-3">
        Anota cada viaje de vaca y búfala. Al aceptar, el día usa la suma de esos viajes.
      </p>
      <div className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center text-xs font-semibold text-gray-500 mb-1">
        <span>Viaje</span>
        <span className="text-center">Vaca</span>
        <span className="text-center">Búfala</span>
        <span />
      </div>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center">
            <span className="text-sm text-center text-gray-600">{index + 1}</span>
            <input
              type="text"
              inputMode="decimal"
              className="w-full text-center text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600"
              value={row.vaca}
              onChange={(e) => updateRow(index, "vaca", e.target.value)}
            />
            <input
              type="text"
              inputMode="decimal"
              className="w-full text-center text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600"
              value={row.bufalo}
              onChange={(e) => updateRow(index, "bufalo", e.target.value)}
            />
            <button
              type="button"
              className="text-xs text-red-600 px-1 py-1"
              onClick={() =>
                setRows((prev) =>
                  prev.length === 1 ? [{ vaca: "", bufalo: "" }] : prev.filter((_, i) => i !== index),
                )
              }
            >
              Quitar
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="mt-3 text-sm font-semibold text-emerald-700"
        onClick={() => setRows((prev) => [...prev, { vaca: "", bufalo: "" }])}
      >
        Agregar viaje
      </button>
      <p className="mt-3 text-sm text-gray-700">
        Total del día: vaca {fmtLiters(totals.vaca)} L · búfala {fmtLiters(totals.bufalo)} L
      </p>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </AppModalShell>
  );
}

export default function QuickDayMilkDialog({
  open,
  onClose,
  providers = [],
  days = [],
  initialDate,
  onSaved,
}) {
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const weekDates = useMemo(() => (days || []).map((d) => d.date).filter(Boolean), [days]);
  const defaultDate = useMemo(() => {
    if (initialDate && weekDates.includes(dateKey(initialDate))) return dateKey(initialDate);
    if (weekDates.includes(todayKey)) return todayKey;
    return weekDates[0] || todayKey;
  }, [initialDate, weekDates, todayKey]);

  const [date, setDate] = useState(defaultDate);
  const [draft, setDraft] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [tripProviderId, setTripProviderId] = useState(null);
  const searchRef = useRef(null);
  const inputRefs = useRef({});
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      const nextDate = defaultDate;
      setDate(nextDate);
      setDraft(buildDraft(providers, nextDate));
      setSearch("");
      setStatus("");
      setSaving(false);
      setTripProviderId(null);
    }
    wasOpen.current = open;
  }, [open, providers, defaultDate]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "f") return;
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return draft;
    return draft.filter((row) => row.name.toLowerCase().includes(term));
  }, [draft, search]);

  const tripRow = useMemo(
    () => (tripProviderId ? draft.find((row) => row.id === tripProviderId) : null),
    [draft, tripProviderId],
  );

  const handleDateChange = (next) => {
    const key = dateKey(next);
    setDate(key);
    setDraft(buildDraft(providers, key));
    setStatus("");
    setTripProviderId(null);
  };

  const setField = (id, field, value) => {
    setDraft((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (row.trips.length) return row;
        return { ...row, [field]: value };
      }),
    );
    setStatus("");
  };

  const applyTrips = (id, trips) => {
    const cleaned = normalizeTrips(trips);
    setDraft((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        if (!cleaned.length) {
          return { ...row, trips: [], vaca: row.vaca, bufalo: row.bufalo };
        }
        return {
          ...row,
          trips: cleaned,
          vaca: litersToInput(sumTrips(cleaned, "vaca")),
          bufalo: litersToInput(sumTrips(cleaned, "bufalo")),
        };
      }),
    );
    setTripProviderId(null);
    setStatus("");
  };

  const focusAt = (index, field) => {
    const row = visible[index];
    if (!row || row.trips.length) return;
    const el = inputRefs.current[`${row.id}:${field}`];
    if (!el) return;
    el.focus();
    el.select();
    el.scrollIntoView({ block: "nearest" });
  };

  const onLitersKeyDown = (event, index, field) => {
    if (event.key !== "Enter" && event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    if (event.key === "ArrowUp") {
      focusAt(index - 1, field);
      return;
    }
    if (index + 1 < visible.length) {
      focusAt(index + 1, field);
      return;
    }
    if (event.key === "Enter") focusAt(0, field);
  };

  const save = async () => {
    const updates = [];
    for (const row of draft) {
      const vacaTyped = String(row.vaca || "").trim();
      const bufaloTyped = String(row.bufalo || "").trim();
      if (vacaTyped && parseQty(row.vaca) == null) {
        toast.error(`Número inválido en vaca (${row.name}).`, { position: "top-center" });
        return;
      }
      if (bufaloTyped && parseQty(row.bufalo) == null) {
        toast.error(`Número inválido en búfala (${row.name}).`, { position: "top-center" });
        return;
      }
      const vaca = parseQty(row.vaca) || 0;
      const bufalo = parseQty(row.bufalo) || 0;
      const trips = normalizeTrips(row.trips);
      const litersChanged = vaca !== row.origVaca || bufalo !== row.origBufalo;
      const tripsChanged = tripKey(trips) !== tripKey(row.origTrips);
      if (!litersChanged && !tripsChanged) continue;
      const split = splitDayDiscount(
        vaca,
        bufalo,
        row.origDiscount || 0,
        row.origDiscountScope || "general",
      );
      updates.push({
        id: row.id,
        name: row.name,
        vaca,
        bufalo,
        trips,
        litersChanged,
        tripsChanged,
        vacaDisc: split.vacaDisc,
        bufaloDisc: split.bufaloDisc,
        discountScope: split.scope,
      });
    }

    if (updates.length === 0) {
      setStatus("No hay cambios que guardar.");
      return;
    }

    setSaving(true);
    setStatus("Guardando…");
    try {
      for (const item of updates) {
        if (item.litersChanged || item.trips.length > 0) {
          await Promise.all([
            upsertMilkDelivery({
              provider: item.id,
              date: `${date}T12:00:00`,
              milkType: "vaca",
              liters: item.vaca,
              discount: item.vacaDisc,
              discountScope: item.discountScope,
            }),
            upsertMilkDelivery({
              provider: item.id,
              date: `${date}T12:00:00`,
              milkType: "bufalo",
              liters: item.bufalo,
              discount: item.bufaloDisc,
              discountScope: item.discountScope,
            }),
          ]);
        }
        if (item.tripsChanged) {
          await upsertMilkDayTrips({
            provider: item.id,
            date: `${date}T12:00:00`,
            trips: item.trips,
          });
        }
      }
      toast.success(`Leche guardada en ${updates.length} proveedores.`, { position: "top-center" });
      if (onSaved) await onSaved(date);
      onClose();
    } catch (error) {
      console.error(error);
      setStatus("No se pudo guardar.");
      toast.error(error.response?.data?.error || "No se pudo guardar la leche del día.", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const label = dayLabel(date, days);

  return (
    <>
      <AppModalShell
        open={open}
        onClose={saving ? undefined : onClose}
        title="Agregar leche del día"
        widthClass="max-w-3xl w-full"
        bodyScroll={false}
        footer={
          <AppModalFooter>
            <span className="text-sm text-gray-500 min-h-[1.25rem]">{status}</span>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button variant="secondary" className="w-full sm:w-auto" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button className="w-full sm:w-auto" onClick={save} disabled={saving}>
                {saving ? "Guardando…" : "Guardar leche"}
              </Button>
            </div>
          </AppModalFooter>
        }
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 pb-3 border-b border-gray-100">
            <p className="text-sm text-gray-500">
              Carga rápido los litros de un día por proveedor. Si un proveedor trajo varios viajes,
              ábrelos con el botón Viajes y la fila usará la suma.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="text-xs text-gray-600">
                Día
                <input
                  type="date"
                  value={date}
                  min={weekDates[0] || undefined}
                  max={weekDates[weekDates.length - 1] || undefined}
                  onChange={(e) => handleDateChange(e.target.value)}
                  disabled={saving}
                  className="mt-1 block w-full min-w-[10.5rem] px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30"
                />
              </label>
              <p className="text-sm font-semibold text-gray-800 pb-1.5">{label}</p>
              {date === todayKey ? (
                <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5 mb-1.5">
                  Hoy
                </span>
              ) : null}
            </div>
            <input
              ref={searchRef}
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  setSearch("");
                  event.currentTarget.blur();
                }
              }}
              placeholder="Buscar proveedor (Ctrl+F)"
              className="mt-3 w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-800 bg-white outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30"
            />
          </div>

          <div className="hidden sm:grid shrink-0 sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6.5rem] gap-2 px-1 pt-3 pb-1 text-xs font-semibold text-gray-500">
            <span>Proveedor</span>
            <span className="text-right">Vaca L</span>
            <span className="text-right">Búfala L</span>
            <span className="text-right">Viajes</span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {visible.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">
                {search.trim() ? "Ningún proveedor coincide con la búsqueda." : "No hay proveedores."}
              </p>
            ) : (
              <div className="divide-y divide-gray-100">
                {visible.map((row, index) => {
                  const locked = row.trips.length > 0;
                  return (
                    <div
                      key={row.id}
                      className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_6.5rem_6.5rem_6.5rem] gap-2 sm:items-center py-2.5"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{row.name}</p>
                        {locked ? (
                          <p className="text-xs text-emerald-700 sm:hidden">
                            {tripButtonLabel(row.trips.length)} · total del día
                          </p>
                        ) : null}
                      </div>
                      <label className="text-xs text-gray-500 sm:text-right">
                        <span className="sm:sr-only">Vaca</span>
                        <input
                          ref={(el) => {
                            inputRefs.current[`${row.id}:vaca`] = el;
                          }}
                          type="text"
                          inputMode="decimal"
                          aria-label={`Litros vaca de ${row.name}`}
                          value={row.vaca}
                          disabled={saving || locked}
                          title={locked ? "Total de los viajes de este día" : undefined}
                          onChange={(event) => setField(row.id, "vaca", event.target.value)}
                          onKeyDown={(event) => onLitersKeyDown(event, index, "vaca")}
                          className={`${inputClass} ${locked ? "bg-gray-100 text-gray-700" : ""}`}
                        />
                      </label>
                      <label className="text-xs text-gray-500 sm:text-right">
                        <span className="sm:sr-only">Búfala</span>
                        <input
                          ref={(el) => {
                            inputRefs.current[`${row.id}:bufalo`] = el;
                          }}
                          type="text"
                          inputMode="decimal"
                          aria-label={`Litros búfala de ${row.name}`}
                          value={row.bufalo}
                          disabled={saving || locked}
                          title={locked ? "Total de los viajes de este día" : undefined}
                          onChange={(event) => setField(row.id, "bufalo", event.target.value)}
                          onKeyDown={(event) => onLitersKeyDown(event, index, "bufalo")}
                          className={`${inputClass} ${locked ? "bg-gray-100 text-gray-700" : ""}`}
                        />
                      </label>
                      <div className="flex sm:justify-end">
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => setTripProviderId(row.id)}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium ${
                            locked
                              ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                          title="Asignar viajes de este proveedor"
                        >
                          <Truck size={14} />
                          {tripButtonLabel(row.trips.length)}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </AppModalShell>

      {tripRow ? (
        <DayTripsEditor
          dayLabelText={`${tripRow.name} · ${label}`}
          initialTrips={tripRow.trips}
          onClose={() => setTripProviderId(null)}
          onApply={(trips) => applyTrips(tripRow.id, trips)}
        />
      ) : null}
    </>
  );
}
